import React from 'react'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'

import { CopyButton } from '@/components/buttons/CopyButton'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'

/**
 * The two things the vendor needs: where to POST, and what to authenticate with.
 *
 * Only the URL can be shown here. The credential is the organization's start/stop
 * token, which the API masks as "**********" like every other secret field, so this
 * panel names it and says where to find it rather than rendering a value that cannot
 * be copied. The URL itself is read straight off the record -- the backend computes
 * `webhook_url` from the same route constants its router decorates with, so the
 * string shown here and the string FastAPI serves cannot drift.
 */
export const GenericWebhookInstructions = () => {
  const { values } = useFormikContext()
  const { webhook_url: url } = values

  if (isEmpty(url)) {
    return (
      <FormField data-testid="generic-webhook">
        <label>Webhook URL</label>
        <div className="form-caption">Available once the integration has been created.</div>
      </FormField>
    )
  }

  return (
    <FormField data-testid="generic-webhook">
      <label>Webhook URL</label>
      <div className="form-caption">
        Give this to the vendor. Every call event is POSTed here as a JSON body, and every path
        below is read from that body. This URL is unique to this integration, so an organization can
        run several &mdash; one per vendor.
      </div>
      <div className="flex-align-center gap">
        {/*
         * readOnly rather than disabled: a disabled input cannot be selected, and some
         * admins paste into a vendor portal by hand.
         */}
        <TextInput
          className="flex-item"
          value={url}
          readOnly
          data-testid="generic-webhook-url"
          aria-label="Webhook URL"
        />
        <CopyButton content={url} name="URL" />
      </div>

      <div className="form-caption" data-testid="generic-webhook-auth">
        The URL says which integration the event belongs to &mdash; it is not the credential. The
        vendor must also send this organization&apos;s start/stop token as an{' '}
        <code>Authorization</code> (or <code>token</code>) header, the same token the other webhook
        connectors use. For a device that can only be given a URL and nothing else, it can go on the
        end instead: <code>?token=&hellip;</code>
        <br />
        The token is on the organization&apos;s page; it is not shown here because the API masks it.
      </div>
    </FormField>
  )
}
